import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, isSession, isTask, parseJson, readError } from "./api";
import type { Agent, AgentActivity, HumanPresence, Output, Session, SessionUser, SummaryProposal, Task, TaskStatus } from "../types";
import { useSseEvent, useSseStatus } from "./useSseEvent";

// How often this browser tells the hub it is still connected. The server reaps a
// human after 30s of silence, so 15s leaves headroom.
const PRESENCE_PING_MS = 15_000;

// A "working" indicator is cleared by an idle/respond event, but if an agent
// dies mid-reply we still expire it after this long with no refresh.
const WORKING_TTL_MS = 90_000;

// sessionId → agentId → epoch ms the agent was last seen "working".
type WorkingMap = Record<string, Record<string, number>>;
// sessionId → agentId → ISO timestamp of the latest message that agent has read.
type SeenMap = Record<string, Record<string, string>>;

// Drop a single agent's working entry; returns the same ref if nothing changed.
function clearWorking(map: WorkingMap, sessionId: string, agentId: string): WorkingMap {
  if (!map[sessionId]?.[agentId]) return map;
  const sess = { ...map[sessionId] };
  delete sess[agentId];
  return { ...map, [sessionId]: sess };
}

// Owns all Agent Hub state and the single SSE connection. The connection lives
// for the app's lifetime (regardless of the active view) so the live indicator
// stays accurate and sessions update in the background.
export interface Hub {
  agents: Record<string, Agent>;
  humans: Record<string, HumanPresence>;
  sessions: Record<string, Session>;
  tasks: Record<string, Task>;
  // False until the session/task board has been fetched at least once. The
  // maps are empty before that, so consumers must NOT read their `.length` as a
  // real count while this is false (0 would be a lie) — treat it as "unknown".
  boardLoaded: boolean;
  connected: boolean;
  activeSessionId: string | null;
  activeOutputs: Output[];
  // Session ids this user opened via a share link (?s=…). A shared session shows
  // in the "Shared with me" group ONLY when it's in here — being shared alone
  // isn't enough (an admin's board holds every session).
  openedViaLinkIds: Set<string>;
  markOpenedViaLink: (id: string) => void;
  selectSession: (id: string) => Promise<void>;
  refreshActiveSession: () => Promise<void>;
  sendNote: (content: string) => Promise<void>;
  saveContext: (content: string) => Promise<void>;
  resolveProposal: (approved: boolean) => Promise<void>;
  createSession: (name?: string, agentIds?: string[]) => Promise<Session>;
  addAgent: (agentId: string) => Promise<void>;
  removeAgent: (agentId: string) => Promise<void>;
  renameSession: (name: string) => Promise<void>;
  deleteSession: (id: string) => Promise<void>;
  tagUser: (user: SessionUser) => Promise<void>;
  untagUser: (sub: string) => Promise<void>;
  shareSession: (shared: boolean) => Promise<void>;
  // Tasks (agent work items, distinct from sessions).
  createTask: (input: { title: string; description: string; org: string | null; assignedAgentIds: string[] }) => Promise<Task>;
  assignTask: (taskId: string, agentIds: string[]) => Promise<void>;
  setTaskStatus: (taskId: string, status: TaskStatus) => Promise<void>;
  addTaskInstruction: (taskId: string, content: string) => Promise<void>;
  deleteTask: (taskId: string) => Promise<void>;
  // Live activity for the active session.
  thinkingAgentIds: string[]; // agents reasoning (seen the latest message) or generating a reply
  seenBy: Record<string, string>; // agentId → ISO timestamp of latest message read
}

export function useHub(): Hub {
  const [agents, setAgents] = useState<Record<string, Agent>>({});
  const [humans, setHumans] = useState<Record<string, HumanPresence>>({});
  const [sessions, setSessions] = useState<Record<string, Session>>({});
  const [tasks, setTasks] = useState<Record<string, Task>>({});
  const [boardLoaded, setBoardLoaded] = useState(false);
  const [connected, setConnected] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [activeOutputs, setActiveOutputs] = useState<Output[]>([]);
  const [openedViaLinkIds, setOpenedViaLinkIds] = useState<Set<string>>(() => new Set());
  const [working, setWorking] = useState<WorkingMap>({});
  const [seen, setSeen] = useState<SeenMap>({});

  // The SSE handlers are bound once; read the latest active id through a ref.
  const activeRef = useRef<string | null>(null);
  activeRef.current = activeSessionId;

  useSseStatus(setConnected);

  // Always-on, lightweight: the agent roster + human presence power the live
  // chrome (TopBar avatars, ChatWidget, the connected dot) on every page, plus
  // the presence heartbeat. The single SSE connection keeps these fresh.
  useEffect(() => {
    Promise.all([api.getAgents(), api.getPresence()])
      .then(([ag, hu]) => {
        setAgents(Object.fromEntries(ag.map((a) => [a.id, a])));
        setHumans(Object.fromEntries(hu.map((h) => [h.id, h])));
      })
      .catch(() => { /* offline — SSE will retry */ });

    void api.presencePing();
    const beat = setInterval(() => void api.presencePing(), PRESENCE_PING_MS);
    return () => clearInterval(beat);
  }, []);

  // Board data (sessions + tasks) backs the global sidebar counts, which show on
  // every view — so it's loaded eagerly once on mount, not lazily per-view; SSE
  // keeps the maps live afterward. The ref guards against a double-fetch (React
  // StrictMode / remounts); a failed fetch resets it so a later mount retries.
  const boardLoadedRef = useRef(false);
  useEffect(() => {
    if (boardLoadedRef.current) return;
    boardLoadedRef.current = true;
    Promise.all([api.getSessions(), api.getTasks()])
      .then(([ss, ts]) => {
        setSessions(Object.fromEntries(ss.map((s) => [s.id, s])));
        setTasks(Object.fromEntries(ts.map((t) => [t.id, t])));
        setBoardLoaded(true);
      })
      .catch(() => { boardLoadedRef.current = false; /* retry on next mount */ });
  }, []);

  useSseEvent<Agent>("agent_update", (agent) => {
    setAgents((prev) => ({ ...prev, [agent.id]: agent }));
    if (agent.status !== "online") {
      setWorking((prev) => {
        let next = prev;
        for (const sessionId of Object.keys(prev)) next = clearWorking(next, sessionId, agent.id);
        return next;
      });
    }
  });

  useSseEvent<AgentActivity>("agent_activity", (activity) => {
    if (activity.state === "idle") {
      setWorking((prev) => clearWorking(prev, activity.sessionId, activity.agentId));
      return;
    }
    setWorking((prev) => ({
      ...prev,
      [activity.sessionId]: { ...(prev[activity.sessionId] || {}), [activity.agentId]: Date.parse(activity.at) || Date.now() },
    }));
    if (activity.state === "seen" && activity.seenAt) {
      setSeen((prev) => ({
        ...prev,
        [activity.sessionId]: { ...(prev[activity.sessionId] || {}), [activity.agentId]: activity.seenAt! },
      }));
    }
  });

  useSseEvent<Session>("session_update", (session) => {
    const { outputs: _outputs, ...rest } = session;
    setSessions((prev) => ({ ...prev, [rest.id]: { ...prev[rest.id], ...rest } }));
  });

  useSseEvent<{ id: string }>("session_deleted", ({ id }) => {
    setSessions((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    if (activeRef.current === id) {
      setActiveSessionId(null);
      setActiveOutputs([]);
    }
  });

  useSseEvent<Output>("new_output", (output) => {
    if (activeRef.current === output.sessionId) setActiveOutputs((prev) => [...prev, output]);
    const kind = output.kind || (output.isHumanNote ? "human" : "agent");
    if (kind === "agent") setWorking((prev) => clearWorking(prev, output.sessionId, output.senderId));
  });

  useSseEvent<SummaryProposal>("proposal_update", (proposal) => {
    setSessions((prev) =>
      prev[proposal.sessionId]
        ? { ...prev, [proposal.sessionId]: { ...prev[proposal.sessionId], pendingProposal: proposal } }
        : prev,
    );
  });

  useSseEvent<HumanPresence>("presence_update", (human) => {
    setHumans((prev) => ({ ...prev, [human.id]: human }));
  });

  useSseEvent<Task>("task_update", (task) => {
    setTasks((prev) => ({ ...prev, [task.id]: task }));
  });

  useSseEvent<{ id: string }>("task_deleted", ({ id }) => {
    setTasks((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  });

  // Remember that a session was opened via its share link, so SessionsColumn can
  // surface it under "Shared with me" (a shared session that was NOT link-opened
  // stays out of that group — it only sits in an admin's global list). New Set on
  // change so the memoized grouping recomputes.
  const markOpenedViaLink = useCallback((id: string) => {
    setOpenedViaLinkIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);

  const selectSession = useCallback(async (id: string) => {
    setActiveSessionId(id);
    setActiveOutputs([]);
    const session = await api.getSession(id);
    const { outputs, ...rest } = session;
    // The transcript is held separately in activeOutputs; keep the board map lean.
    setSessions((prev) => ({ ...prev, [id]: { ...rest, outputs: [] } }));
    setActiveOutputs(outputs || []);
    // Seed read receipts from the persisted session (live events refine them).
    if (session.seenBy) {
      setSeen((prev) => ({ ...prev, [id]: { ...session.seenBy, ...(prev[id] || {}) } }));
    }
  }, []);

  // Re-fetch the active session and refresh its transcript IN PLACE — unlike
  // selectSession it doesn't clear outputs first (no flicker), and it skips the
  // state update entirely when nothing changed (no re-render / re-scroll on idle
  // polls). Backs the pure-viewer poll in AgentHub: a link viewer isn't in the
  // session's SSE audience, so this is how they see new messages until they post
  // a note (which tags them → they become a participant → SSE takes over).
  const refreshActiveSession = useCallback(async () => {
    const id = activeRef.current;
    if (!id) return;
    const session = await api.getSession(id);
    const { outputs, ...rest } = session;
    setSessions((prev) => ({ ...prev, [id]: { ...prev[id], ...rest, outputs: [] } }));
    setActiveOutputs((prev) => {
      const next = outputs || [];
      if (prev.length === next.length && prev[prev.length - 1]?.id === next[next.length - 1]?.id) return prev;
      return next;
    });
    if (session.seenBy) {
      setSeen((prev) => ({ ...prev, [id]: { ...session.seenBy, ...(prev[id] || {}) } }));
    }
  }, []);

  const sendNote = useCallback(async (content: string) => {
    const id = activeRef.current;
    if (!id || !content.trim()) return;
    await api.postNote(id, content.trim());
  }, []);

  const saveContext = useCallback(async (content: string) => {
    const id = activeRef.current;
    if (!id) return;
    await api.postContext(id, content);
    setSessions((prev) => (prev[id] ? { ...prev, [id]: { ...prev[id], context: content } } : prev));
  }, []);

  const resolveProposal = useCallback(async (approved: boolean) => {
    const id = activeRef.current;
    if (!id) return;
    await api.postApprove(id, approved);
  }, []);

  const createSession = useCallback(
    async (name?: string, agentIds: string[] = []) => {
      const res = await api.createSession(name, agentIds);
      if (!res.ok) throw new Error(await readError(res, "Failed to create session"));
      const session = await parseJson(res, isSession);
      setSessions((prev) => ({ ...prev, [session.id]: session }));
      await selectSession(session.id);
      return session;
    },
    [selectSession],
  );

  const addAgent = useCallback(async (agentId: string) => {
    const id = activeRef.current;
    if (!id) return;
    await api.addSessionAgent(id, agentId);
  }, []);

  const removeAgent = useCallback(async (agentId: string) => {
    const id = activeRef.current;
    if (!id) return;
    await api.removeSessionAgent(id, agentId);
  }, []);

  const renameSession = useCallback(async (name: string) => {
    const id = activeRef.current;
    if (!id || !name.trim()) return;
    await api.renameSession(id, name.trim());
  }, []);

  const deleteSession = useCallback(async (id: string) => {
    if (!id) return;
    const res = await api.deleteSession(id);
    if (!res.ok && res.status !== 404) throw new Error(await readError(res, "Failed to delete session"));
    // Optimistic local removal (the session_deleted SSE event also covers other clients).
    setSessions((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    if (activeRef.current === id) {
      setActiveSessionId(null);
      setActiveOutputs([]);
    }
  }, []);

  // Merge a tag change into the board map (the SSE session_update covers other
  // clients; this keeps the acting client instant). The response is the full
  // session — strip outputs to keep the map lean, matching selectSession.
  const applyTaggedUpdate = useCallback((session: Session) => {
    const { outputs: _o, ...rest } = session;
    setSessions((prev) => ({ ...prev, [rest.id]: { ...prev[rest.id], ...rest } }));
  }, []);

  const tagUser = useCallback(async (user: SessionUser) => {
    const id = activeRef.current;
    if (!id) return;
    const res = await api.addSessionTag(id, user);
    if (!res.ok) throw new Error(await readError(res, "Failed to tag user"));
    applyTaggedUpdate(await parseJson(res, isSession));
  }, [applyTaggedUpdate]);

  const untagUser = useCallback(async (sub: string) => {
    const id = activeRef.current;
    if (!id) return;
    const res = await api.removeSessionTag(id, sub);
    if (!res.ok) throw new Error(await readError(res, "Failed to remove tag"));
    applyTaggedUpdate(await parseJson(res, isSession));
  }, [applyTaggedUpdate]);

  const shareSession = useCallback(async (shared: boolean) => {
    const id = activeRef.current;
    if (!id) return;
    const res = await api.shareSession(id, shared);
    if (!res.ok) throw new Error(await readError(res, "Failed to update sharing"));
    applyTaggedUpdate(await parseJson(res, isSession));
  }, [applyTaggedUpdate]);

  const createTask = useCallback(
    async (input: { title: string; description: string; org: string | null; assignedAgentIds: string[] }) => {
      const res = await api.createTask(input);
      if (!res.ok) throw new Error(await readError(res, "Failed to create task"));
      const task = await parseJson(res, isTask);
      setTasks((prev) => ({ ...prev, [task.id]: task }));
      return task;
    },
    [],
  );

  const assignTask = useCallback(async (taskId: string, agentIds: string[]) => {
    await api.assignTask(taskId, agentIds);
  }, []);

  const setTaskStatus = useCallback(async (taskId: string, status: TaskStatus) => {
    await api.setTaskStatus(taskId, status);
  }, []);

  const addTaskInstruction = useCallback(async (taskId: string, content: string) => {
    if (!content.trim()) return;
    const res = await api.addTaskInstruction(taskId, content.trim());
    if (!res.ok) throw new Error(await readError(res, "Failed to add instruction"));
    const task = await parseJson(res, isTask);
    setTasks((prev) => ({ ...prev, [task.id]: task }));
  }, []);

  const deleteTask = useCallback(async (taskId: string) => {
    if (!taskId) return;
    const res = await api.deleteTask(taskId);
    if (!res.ok && res.status !== 404) throw new Error(await readError(res, "Failed to delete task"));
    // Optimistic local removal (the task_deleted SSE event also covers other clients).
    setTasks((prev) => {
      if (!prev[taskId]) return prev;
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
  }, []);

  // Expire stale "working" entries (agent died without an idle/respond). Only
  // ticks while something is working, and returns the same ref when nothing
  // changes so it doesn't force re-renders.
  useEffect(() => {
    const hasWork = Object.values(working).some((s) => Object.keys(s).length);
    if (!hasWork) return;
    const t = setInterval(() => {
      const cutoff = Date.now() - WORKING_TTL_MS;
      setWorking((prev) => {
        let changed = false;
        const next: WorkingMap = {};
        for (const [sid, byAgent] of Object.entries(prev)) {
          const kept: Record<string, number> = {};
          for (const [aid, at] of Object.entries(byAgent)) {
            if (at >= cutoff) kept[aid] = at;
            else changed = true;
          }
          next[sid] = kept;
        }
        return changed ? next : prev;
      });
    }, 10_000);
    return () => clearInterval(t);
  }, [working]);

  const thinkingAgentIds = useMemo(() => {
    const byAgent = activeSessionId ? working[activeSessionId] || {} : {};
    const cutoff = Date.now() - WORKING_TTL_MS;
    return Object.entries(byAgent).filter(([, at]) => at >= cutoff).map(([id]) => id);
  }, [working, activeSessionId]);
  const seenBy = activeSessionId ? seen[activeSessionId] || {} : {};

  return {
    agents, humans, sessions, tasks, boardLoaded, connected, activeSessionId, activeOutputs,
    openedViaLinkIds, markOpenedViaLink,
    selectSession, refreshActiveSession, sendNote, saveContext, resolveProposal, createSession,
    addAgent, removeAgent, renameSession, deleteSession, tagUser, untagUser, shareSession,
    createTask, assignTask, setTaskStatus, addTaskInstruction, deleteTask,
    thinkingAgentIds, seenBy,
  };
}
